import { Fragment, useState } from 'react';

import { LEGACY_EXCLUDED_FORM_ELEMENT_TYPES } from '@gearbelt/global';
import { format } from 'date-fns';

import CheckCircleIcon from '@/components/icons/check-circle';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import { Lightbox } from '@/components/ui/lightbox';
import { FormElementType } from '@/models/api';
import { convertFullViewImageUrl, convertImageUrl } from '@/utils/image-url';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function LegacySubmissionRenderer({ data, location, createdAt }: { data: any[]; location?: string; createdAt?: string }) {
  const [showLightbox, setShowLightbox] = useState<string[] | undefined>();

  return (
    <>
      <div className="list-group list-group-flush">
        {location && (
          <div className="list-group-item mb-4">
            <h5 className="text-secondary-text text-xs">Location:</h5>
            <p className="text-sm font-medium">{location}</p>
          </div>
        )}

        {createdAt && (
          <div className="list-group-item mb-4">
            <h5 className="text-secondary-text text-xs">Submitted At:</h5>
            <p className="text-sm font-medium">
              {(() => {
                const date = new Date(createdAt);
                return !isNaN(date.getTime()) ? format(date, 'MM/dd/yyyy HH:mm') : createdAt;
              })()}
            </p>
          </div>
        )}

        {data
          .filter(
            (submission) =>
              !(submission.type === FormElementType.Choice && submission.value === 'NotAvailable') && !LEGACY_EXCLUDED_FORM_ELEMENT_TYPES.has(submission.type),
          )
          .map((submission, i) => (
            <Fragment key={i}>
              <div className="list-group-item">
                {submission.type === FormElementType.Checkbox && (
                  <div className="flex w-full items-center justify-between">
                    <h5 className="flex items-center gap-1">
                      {submission.value ? (
                        <span className="text-default-green">
                          <CheckCircleIcon className="mr-2" />
                        </span>
                      ) : (
                        <span className="text-default-orange">
                          <WarningTriangleIcon className="mr-2" />
                        </span>
                      )}
                      {submission.label}
                    </h5>
                  </div>
                )}

                {submission.type === FormElementType.Choice && (
                  <>
                    <div className="flex w-full items-center justify-between">
                      <h5 className="flex items-center gap-1">
                        {submission.notes || submission.value === 'No' ? (
                          <span className="text-default-orange">
                            <WarningTriangleIcon className="mr-2" />
                          </span>
                        ) : (
                          <span className="text-default-green">
                            <CheckCircleIcon className="mr-2" />
                          </span>
                        )}
                        {submission.label}
                      </h5>
                      {submission.completedAt && <small className="text-secondary-text">{format(submission.completedAt, 'HH:mm:ss')}</small>}
                    </div>
                    {submission.notes && (
                      <div className="border-primary my-2 ml-6 flex items-center border-l-4 pl-2">
                        {submission.notes?.split(/\n/).map((note: string, i: number) => (
                          <blockquote key={i} className="m-1">
                            {note}
                          </blockquote>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {(submission.type === FormElementType.Text || submission.type === FormElementType.Number || submission.type === FormElementType.Date) && (
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-1">
                      <span className="text-default-green">
                        <CheckCircleIcon className="mr-2" />
                      </span>
                      <b>{submission.label}</b>: {submission.value.toString()}
                    </div>
                    {submission.completedAt && <small className="text-secondary-text">{format(submission.completedAt, 'HH:mm:ss')}</small>}
                  </div>
                )}

                {submission.type === FormElementType.TextArea && (
                  <div>
                    <div className="flex items-center gap-1">
                      <span className="text-default-green">
                        <CheckCircleIcon className="mr-2" />
                      </span>
                      <b>{submission.label}</b>
                    </div>
                    {submission.value && (
                      <div className="my-2 ml-6 border-l-4 border-[rgb(var(--default-green))] pl-2">
                        {submission.value?.split(/\n/).map((note: string, i: number) => (
                          <blockquote key={i} className="m-1">
                            {note}
                          </blockquote>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {submission.type === FormElementType.Heading && (
                  <h5 className="">
                    <b>{submission.label.toUpperCase()}</b>
                  </h5>
                )}

                {submission.type === FormElementType.Canvas && (
                  <div className="p-2">
                    <img src={convertImageUrl(submission.value)} className="max-h-[100px] max-w-[90%]" alt="Signature" />
                  </div>
                )}

                {submission.photos && (
                  <div className="flex flex-wrap items-start gap-2 p-1">
                    {submission.photos
                      ?.split('\n')
                      .map((photo: string, i: number) => (
                        <img
                          key={i}
                          src={convertImageUrl(photo, { width: 200, height: 200 })}
                          className="max-h-[200px] max-w-[200px] cursor-pointer"
                          onClick={() => setShowLightbox(submission.photos?.split('\n'))}
                        />
                      ))}
                  </div>
                )}
              </div>

              <hr className="my-1" />
            </Fragment>
          ))}
      </div>
      {showLightbox && <Lightbox photos={showLightbox.map((photo) => convertFullViewImageUrl(photo))} onHide={() => setShowLightbox(undefined)} />}
    </>
  );
}
